import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  locationTaskStatusQueryOptions,
  useTriggerDetectMutation,
  useTriggerBackfillMutation,
} from '@/lib/query-options';

interface DetectorStatusProps {
  businessId: number;
  /** Current row count, retained for parity with caller signature. */
  currentRowCount: number;
  render: (props: {
    onRunDetect: () => void;
    onBackfill: () => void;
    isRunning: boolean;
    isStarting: boolean;
  }) => ReactNode;
}

const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes runaway protection

/**
 * Drives the async location tasks (AI detect + history backfill). Both enqueue a
 * Celery task and return a task_id; this polls the shared status endpoint and
 * refreshes the locations table (+ business, for the backfill truncation flag) on
 * completion. #5502: the backfill previously fire-and-forgot and invalidated the
 * wrong query, so the table never updated — it now shares this polling path.
 */
export function DetectorStatus({ businessId, render }: DetectorStatusProps) {
  const queryClient = useQueryClient();
  const [taskId, setTaskId] = useState<number | null>(null);

  const detect = useTriggerDetectMutation(businessId);
  const backfill = useTriggerBackfillMutation(businessId);
  const status = useQuery(locationTaskStatusQueryOptions(businessId, taskId));

  useEffect(() => {
    if (!taskId) return;
    const timer = setTimeout(() => setTaskId(null), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [taskId]);

  useEffect(() => {
    if (!taskId || !status.data) return;
    if (status.data.state === 'COMPLETED') {
      queryClient.invalidateQueries({ queryKey: ['business-locations', businessId] });
      queryClient.invalidateQueries({ queryKey: ['business', businessId] });
      setTaskId(null);
    } else if (status.data.state === 'FAILED') {
      setTaskId(null);
    }
  }, [status.data, taskId, queryClient, businessId]);

  const onRunDetect = async () => {
    const { task_id } = await detect.mutateAsync();
    setTaskId(task_id);
  };
  const onBackfill = async () => {
    const { task_id } = await backfill.mutateAsync();
    setTaskId(task_id);
  };

  const isRunning = !!taskId && status.data?.state === 'IN_PROGRESS';
  const isStarting = detect.isPending || backfill.isPending;

  return <>{render({ onRunDetect, onBackfill, isRunning, isStarting })}</>;
}
